import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar, MapPin, Clock, Users, Heart, Settings, ChevronDown, ChevronUp, GraduationCap, ExternalLink, CalendarPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistType, setWaitlistType] = useState('student');
  const [expandedSignups, setExpandedSignups] = useState({});
  const [publicSignups, setPublicSignups] = useState({});
  const [waitlistCounts, setWaitlistCounts] = useState({});
  const [signupForm, setSignupForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    age_grade: '',
    volunteer_type: 'student'
  });
  const [waitlistForm, setWaitlistForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${API}/opportunities`);
      setOpportunities(response.data);
      // Fetch waitlist counts for all opportunities
      response.data.forEach(opp => fetchWaitlistCount(opp.id));
    } catch (error) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const fetchWaitlistCount = async (oppId) => {
    try {
      const response = await axios.get(`${API}/waitlist/${oppId}/public`);
      setWaitlistCounts(prev => ({ ...prev, [oppId]: response.data }));
    } catch (error) {
      console.error('Failed to load waitlist count');
    }
  };

  const fetchPublicSignups = async (oppId) => {
    if (publicSignups[oppId]) return; // Already loaded
    try {
      const response = await axios.get(`${API}/signups/${oppId}/public`);
      setPublicSignups(prev => ({ ...prev, [oppId]: response.data }));
    } catch (error) {
      console.error('Failed to load signups');
    }
  };

  const toggleSignups = (oppId) => {
    const isExpanding = !expandedSignups[oppId];
    setExpandedSignups(prev => ({ ...prev, [oppId]: isExpanding }));
    if (isExpanding) {
      fetchPublicSignups(oppId);
    }
  };

  const handleSignupClick = (opp) => {
    setSelectedOpp(opp);
    setSignupForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      age_grade: '',
      volunteer_type: 'student'
    });
    setShowSignupModal(true);
  };

  const handleWaitlistClick = (opp, type) => {
    setSelectedOpp(opp);
    setWaitlistType(type);
    setWaitlistForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    });
    setShowWaitlistModal(true);
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        ...waitlistForm,
        opportunity_id: selectedOpp.id,
        volunteer_type: waitlistType
      });
      toast.success('You\'ve been added to the waitlist! We\'ll notify you if a spot opens.');
      setShowWaitlistModal(false);
      fetchWaitlistCount(selectedOpp.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setSubmitting(false);
    }
  };

  const generateGoogleCalendarUrl = (opp) => {
    const startDate = new Date(opp.date);
    // Parse time like "9:00 AM - 12:00 PM" or "2:00 PM"
    const timeMatch = opp.time.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2] || '0');
      const period = timeMatch[3]?.toUpperCase();
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      startDate.setHours(hours, minutes, 0, 0);
    }
    
    // End time is 2 hours after start by default
    const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    
    const formatDate = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: opp.title,
      details: opp.description,
      location: opp.location,
      dates: `${formatDate(startDate)}/${formatDate(endDate)}`
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/signups`, {
        ...signupForm,
        opportunity_id: selectedOpp.id
      });
      toast.success('Successfully signed up! Check your email for confirmation.');
      setShowSignupModal(false);
      // Refresh opportunities and clear cached signups to show updated list
      fetchOpportunities();
      setPublicSignups(prev => {
        const updated = { ...prev };
        delete updated[selectedOpp.id];
        return updated;
      });
      // Auto-expand to show the new signup
      setExpandedSignups(prev => ({ ...prev, [selectedOpp.id]: true }));
      fetchPublicSignups(selectedOpp.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up');
    } finally {
      setSubmitting(false);
    }
  };

  const getAvailableSlots = (opp, type) => {
    if (type === 'student') {
      return opp.student_slots - opp.student_signups;
    }
    return opp.parent_slots - opp.parent_signups;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Decorative blobs */}
      <div className="bg-blob w-96 h-96 bg-primary -top-48 -left-48 fixed" />
      <div className="bg-blob w-80 h-80 bg-secondary top-1/2 -right-40 fixed" />
      
      {/* Header */}
      <header className="relative z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-extrabold text-foreground font-[Nunito]">GYVC | VolunteerHub</h1>
          </div>
          <Link to="/admin/login">
            <Button variant="ghost" size="sm" className="gap-2" data-testid="admin-login-link">
              <Settings className="w-4 h-4" />
              Admin
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 font-[Nunito] tracking-tight animate-slide-up">
            Make a Difference in Your Community
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '100ms' }}>
            Find volunteer opportunities for middle school GYVC members that match your schedule. Sign up easily and start making an impact today.
          </p>
        </div>
      </section>

      {/* Opportunities Grid */}
      <section className="relative z-10 px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground font-[Nunito]">
              Available Opportunities
            </h3>
            <Badge variant="secondary" className="text-sm px-4 py-1" data-testid="opportunities-count">
              {opportunities.length} {opportunities.length === 1 ? 'Event' : 'Events'}
            </Badge>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="rounded-3xl animate-pulse">
                  <CardHeader className="space-y-4">
                    <div className="h-6 bg-muted rounded w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-full"></div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                    <div className="h-10 bg-muted rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : opportunities.length === 0 ? (
            <div className="empty-state bg-card rounded-3xl border border-border/50 py-16">
              <Users className="empty-state-icon text-muted-foreground mx-auto" />
              <h4 className="text-xl font-bold text-foreground mb-2 font-[Nunito]">No Opportunities Yet</h4>
              <p className="text-muted-foreground">Check back soon for new volunteer opportunities!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
              {opportunities.map((opp, index) => (
                <Card 
                  key={opp.id} 
                  className="opportunity-card rounded-3xl border-border/50 shadow-sm hover:shadow-lg animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`opportunity-card-${opp.id}`}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <CardTitle className="text-xl font-bold text-foreground font-[Nunito] leading-tight">
                        {opp.title}
                      </CardTitle>
                    </div>
                    <CardDescription className="text-muted-foreground line-clamp-2 mt-2">
                      {opp.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-foreground">
                        <Calendar className="w-4 h-4 text-primary" />
                        <span>{formatDate(opp.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-foreground">
                        <Clock className="w-4 h-4 text-primary" />
                        <span>{opp.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-foreground">
                        <MapPin className="w-4 h-4 text-primary" />
                        <span className="truncate">{opp.location}</span>
                      </div>
                    </div>

                    <div className="space-y-3 pt-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Student Spots</span>
                        <span className="font-semibold text-foreground">
                          {getAvailableSlots(opp, 'student')}/{opp.student_slots}
                        </span>
                      </div>
                      <div className="slot-progress">
                        <div 
                          className="slot-progress-fill bg-primary" 
                          style={{ width: `${(opp.student_signups / Math.max(opp.student_slots, 1)) * 100}%` }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Parent Spots</span>
                        <span className="font-semibold text-foreground">
                          {getAvailableSlots(opp, 'parent')}/{opp.parent_slots}
                        </span>
                      </div>
                      <div className="slot-progress">
                        <div 
                          className="slot-progress-fill bg-secondary" 
                          style={{ width: `${(opp.parent_signups / Math.max(opp.parent_slots, 1)) * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* Who's Signed Up Section */}
                    {(opp.student_signups > 0 || opp.parent_signups > 0) && (
                      <div className="pt-2">
                        <button
                          type="button"
                          onClick={() => toggleSignups(opp.id)}
                          className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors w-full justify-center py-2"
                          data-testid={`toggle-signups-${opp.id}`}
                        >
                          <Users className="w-4 h-4" />
                          <span>Who's Signed Up ({opp.student_signups + opp.parent_signups})</span>
                          {expandedSignups[opp.id] ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        
                        {expandedSignups[opp.id] && (
                          <div className="mt-3 bg-accent/50 rounded-xl p-3 space-y-2 animate-fade-in">
                            {publicSignups[opp.id] ? (
                              publicSignups[opp.id].length > 0 ? (
                                <div className="space-y-1">
                                  {publicSignups[opp.id].map((signup, idx) => (
                                    <div 
                                      key={idx} 
                                      className="flex items-center justify-between text-sm py-1"
                                    >
                                      <span className="text-foreground">{signup.display_name}</span>
                                      <Badge 
                                        variant={signup.volunteer_type === 'student' ? 'default' : 'secondary'}
                                        className="text-xs capitalize"
                                      >
                                        {signup.volunteer_type === 'student' ? (
                                          <><GraduationCap className="w-3 h-3 mr-1" />Student</>
                                        ) : (
                                          <><Users className="w-3 h-3 mr-1" />Parent</>
                                        )}
                                      </Badge>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground text-center">No sign-ups yet</p>
                              )
                            ) : (
                              <div className="flex justify-center py-2">
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent"></div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <Button 
                      onClick={() => handleSignupClick(opp)}
                      className="w-full rounded-full font-bold mt-4 transition-transform hover:scale-105 active:scale-95"
                      disabled={getAvailableSlots(opp, 'student') <= 0 && getAvailableSlots(opp, 'parent') <= 0}
                      data-testid={`signup-btn-${opp.id}`}
                    >
                      {getAvailableSlots(opp, 'student') <= 0 && getAvailableSlots(opp, 'parent') <= 0 
                        ? 'Fully Booked' 
                        : 'Sign Up Now'}
                    </Button>

                    {/* Waitlist buttons when slots are full */}
                    {(getAvailableSlots(opp, 'student') <= 0 || getAvailableSlots(opp, 'parent') <= 0) && (
                      <div className="flex gap-2 mt-2">
                        {getAvailableSlots(opp, 'student') <= 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleWaitlistClick(opp, 'student')}
                            className="flex-1 rounded-full text-xs"
                            data-testid={`waitlist-student-${opp.id}`}
                          >
                            <GraduationCap className="w-3 h-3 mr-1" />
                            Join Student Waitlist
                            {waitlistCounts[opp.id]?.student_waitlist > 0 && (
                              <Badge variant="secondary" className="ml-1 text-xs px-1">
                                {waitlistCounts[opp.id].student_waitlist}
                              </Badge>
                            )}
                          </Button>
                        )}
                        {getAvailableSlots(opp, 'parent') <= 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleWaitlistClick(opp, 'parent')}
                            className="flex-1 rounded-full text-xs"
                            data-testid={`waitlist-parent-${opp.id}`}
                          >
                            <Users className="w-3 h-3 mr-1" />
                            Join Parent Waitlist
                            {waitlistCounts[opp.id]?.parent_waitlist > 0 && (
                              <Badge variant="secondary" className="ml-1 text-xs px-1">
                                {waitlistCounts[opp.id].parent_waitlist}
                              </Badge>
                            )}
                          </Button>
                        )}
                      </div>
                    )}

                    {/* External Links Row */}
                    <div className="flex gap-2 mt-3">
                      {opp.signup_genius_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          className="flex-1 rounded-full text-xs"
                          data-testid={`signup-genius-${opp.id}`}
                        >
                          <a href={opp.signup_genius_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-3 h-3 mr-1" />
                            SignUpGenius
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className={`${opp.signup_genius_url ? 'flex-1' : 'w-full'} rounded-full text-xs`}
                        data-testid={`add-to-calendar-${opp.id}`}
                      >
                        <a href={generateGoogleCalendarUrl(opp)} target="_blank" rel="noopener noreferrer">
                          <CalendarPlus className="w-3 h-3 mr-1" />
                          Add to Calendar
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Signup Modal */}
      <Dialog open={showSignupModal} onOpenChange={setShowSignupModal}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="signup-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-[Nunito]">
              Volunteer Sign Up
            </DialogTitle>
            <DialogDescription>
              {selectedOpp?.title}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSignupSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={signupForm.first_name}
                  onChange={(e) => setSignupForm(f => ({ ...f, first_name: e.target.value }))}
                  required
                  className="rounded-xl h-11"
                  data-testid="signup-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={signupForm.last_name}
                  onChange={(e) => setSignupForm(f => ({ ...f, last_name: e.target.value }))}
                  required
                  className="rounded-xl h-11"
                  data-testid="signup-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={signupForm.email}
                onChange={(e) => setSignupForm(f => ({ ...f, email: e.target.value }))}
                required
                className="rounded-xl h-11"
                data-testid="signup-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={signupForm.phone}
                onChange={(e) => setSignupForm(f => ({ ...f, phone: e.target.value }))}
                required
                className="rounded-xl h-11"
                data-testid="signup-phone"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="age_grade">Age/Grade</Label>
              <Input
                id="age_grade"
                value={signupForm.age_grade}
                onChange={(e) => setSignupForm(f => ({ ...f, age_grade: e.target.value }))}
                placeholder="e.g., 10th Grade, Adult"
                required
                className="rounded-xl h-11"
                data-testid="signup-age-grade"
              />
            </div>
            
            <div className="space-y-3">
              <Label>I am a...</Label>
              <RadioGroup
                value={signupForm.volunteer_type}
                onValueChange={(value) => setSignupForm(f => ({ ...f, volunteer_type: value }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value="student" 
                    id="student" 
                    disabled={selectedOpp && getAvailableSlots(selectedOpp, 'student') <= 0}
                    data-testid="signup-type-student"
                  />
                  <Label htmlFor="student" className="cursor-pointer">
                    Student ({selectedOpp ? getAvailableSlots(selectedOpp, 'student') : 0} spots)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value="parent" 
                    id="parent"
                    disabled={selectedOpp && getAvailableSlots(selectedOpp, 'parent') <= 0}
                    data-testid="signup-type-parent"
                  />
                  <Label htmlFor="parent" className="cursor-pointer">
                    Parent ({selectedOpp ? getAvailableSlots(selectedOpp, 'parent') : 0} spots)
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowSignupModal(false)}
                className="flex-1 rounded-full"
                data-testid="signup-cancel-btn"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1 rounded-full font-bold"
                data-testid="signup-submit-btn"
              >
                {submitting ? 'Signing up...' : 'Sign Up'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Waitlist Modal */}
      <Dialog open={showWaitlistModal} onOpenChange={setShowWaitlistModal}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="waitlist-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-[Nunito]">
              Join Waitlist
            </DialogTitle>
            <DialogDescription>
              {selectedOpp?.title} - {waitlistType === 'student' ? 'Student' : 'Parent'} Waitlist
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleWaitlistSubmit} className="space-y-4 mt-4">
            <div className="bg-accent/50 rounded-xl p-3 text-sm text-muted-foreground">
              <p>All {waitlistType} spots are currently filled. Join the waitlist and we'll notify you by email if a spot opens up!</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="waitlist_first_name">First Name</Label>
                <Input
                  id="waitlist_first_name"
                  value={waitlistForm.first_name}
                  onChange={(e) => setWaitlistForm(f => ({ ...f, first_name: e.target.value }))}
                  required
                  className="rounded-xl h-11"
                  data-testid="waitlist-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="waitlist_last_name">Last Name</Label>
                <Input
                  id="waitlist_last_name"
                  value={waitlistForm.last_name}
                  onChange={(e) => setWaitlistForm(f => ({ ...f, last_name: e.target.value }))}
                  required
                  className="rounded-xl h-11"
                  data-testid="waitlist-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="waitlist_email">Email</Label>
              <Input
                id="waitlist_email"
                type="email"
                value={waitlistForm.email}
                onChange={(e) => setWaitlistForm(f => ({ ...f, email: e.target.value }))}
                required
                className="rounded-xl h-11"
                data-testid="waitlist-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="waitlist_phone">Phone</Label>
              <Input
                id="waitlist_phone"
                type="tel"
                value={waitlistForm.phone}
                onChange={(e) => setWaitlistForm(f => ({ ...f, phone: e.target.value }))}
                required
                className="rounded-xl h-11"
                data-testid="waitlist-phone"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowWaitlistModal(false)}
                className="flex-1 rounded-full"
                data-testid="waitlist-cancel-btn"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1 rounded-full font-bold"
                data-testid="waitlist-submit-btn"
              >
                {submitting ? 'Joining...' : 'Join Waitlist'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
